/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Carcasa scroll para módulos operativos (Catálogo / Stock / Ventas…).
 * Encapsula el boilerplate compartido:
 *   ShellContentFrame → operationalTabListHost → ScrollView(Refresh) → stack(header, notices, body)
 *   + modals como siblings fuera del scroll.
 *
 * El caller provee header, notices, body y modals como slots.
 * No toca lógica de negocio — solo layout.
 */
import React, { type Ref } from 'react';
import {
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { spacing, ticketColors } from '../theme/public-api';
import { ShellContentFrame } from '../layout/ShellContentFrame';
import { operationalTabListHostStyle } from '../layout/operationalTabListHost';

export type OperationalScrollScreenProps = {
  /** Header del módulo (CatalogScreenHeader, InventoryHeaderSection, …). */
  header?: React.ReactNode;
  /** Alertas / banners entre header y lista (alert strips, pending transfers, …). */
  notices?: React.ReactNode;
  /** Cuerpo del scroll: PaperSheet con lista + paginación, empty states, etc. */
  body?: React.ReactNode;
  /** Modales / sheets renderizados como siblings fuera del ShellContentFrame. */
  modals?: React.ReactNode;
  /** Estado vacío full-screen que reemplaza todo el scroll (ej. "sin sucursal"). */
  fullEmptyState?: React.ReactNode;

  /** ScrollView ref (programmatic scroll). */
  scrollRef?: Ref<ScrollView>;
  /** Pull-to-refresh. */
  refreshing?: boolean;
  onRefresh?: () => void;
  /** contentContainerStyle del ScrollView. */
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** style del ScrollView (ej. flex: 1). */
  scrollStyle?: StyleProp<ViewStyle>;
  /** Gap entre bloques del stack. Default 10. */
  stackGap?: number;

  /** ShellContentFrame style (container + padding del módulo). */
  containerStyle?: StyleProp<ViewStyle>;
  /** ShellContentFrame style cuando NO es wide (compact padding). */
  containerCompactStyle?: StyleProp<ViewStyle>;
  /** Wide layout flag. */
  layoutWide?: boolean;

  testID?: string;
};

export function OperationalScrollScreen({
  header,
  notices,
  body,
  modals,
  fullEmptyState,
  scrollRef,
  refreshing,
  onRefresh,
  contentContainerStyle,
  scrollStyle,
  stackGap = 10,
  containerStyle,
  containerCompactStyle,
  layoutWide = false,
  testID,
}: OperationalScrollScreenProps) {
  const scroll = (
    <View style={operationalTabListHostStyle.host}>
      <View style={{ flex: 1, minHeight: 0 }}>
        <ScrollView
          ref={scrollRef}
          style={[styles.scroll, scrollStyle]}
          contentContainerStyle={contentContainerStyle}
          refreshControl={
            onRefresh ? (
              <RefreshControl
                refreshing={refreshing ?? false}
                onRefresh={onRefresh}
                colors={[ticketColors.ink2]}
                tintColor={ticketColors.ink2}
              />
            ) : undefined
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.stack, { gap: stackGap }]}>
            {header}
            {notices}
            {body}
          </View>
        </ScrollView>
      </View>
    </View>
  );

  return (
    <>
      <ShellContentFrame
        style={[containerStyle, !layoutWide && containerCompactStyle]}
        testID={testID}
      >
        {fullEmptyState ?? scroll}
      </ShellContentFrame>
      {modals}
    </>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  stack: {
    gap: spacing.sm,
  },
});
